/**
 * Knowledge page — RAG document management for the customer admin.
 *
 * After Phase 3 of the RAG knowledge initiative, the onboarding admin
 * lands here, sees a list of uploaded policy docs with live status,
 * drags-and-drops new files, and deletes outdated ones. The agent picks
 * up the chunks via `search_rag` and answers in Teams with inline
 * citations.
 *
 * Branches:
 *   - me loading / error          → spinner / generic error
 *   - me.result === not_onboarded → reuse NotOnboardedScreen pattern
 *   - me.is_onboarding_admin === false → ForbiddenScreen
 *   - empty list                  → KnowledgeEmptyState + upload modal
 *   - populated list              → KnowledgeList + upload modal
 *
 * Polling: while any doc is in `pending` or `embedding`, the list is
 * refetched every 5s. Hooked off the visible list state, not a global
 * interval — leaves the network alone once everything settles.
 *
 * The consent modal is shown on the FIRST upload attempt for tenants
 * whose `consent_acknowledged_at` is still null; consenting calls
 * POST /portal/api/v1/rag/consent and refreshes `/me`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import { useMsal } from "@azure/msal-react";
import { acquireIdToken } from "@/auth/msal";
import {
  deleteRagDocument,
  getMe,
  listRagDocuments,
  postRagConsent,
  uploadRagDocument,
  type MeResult,
  type RagDocStatus,
  type RagDocument,
  type RagDocumentList,
  type RagUploadError,
} from "@/api/portal";
import { Button, Card, Icon, Spinner } from "@/components/ui";
import { useI18n } from "@/lib/i18n";

// Locked client-side guardrails — mirror the server caps so the user
// sees the rejection without waiting on a round trip. The server
// re-enforces these and is the authoritative gate.
const MAX_DOC_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".md", ".txt"] as const;
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown",
  "text/plain",
] as const;
const POLL_INTERVAL_MS = 5000;

type IdTokenFetcher = () => Promise<string | null>;

// ---- helpers ---------------------------------------------------------------

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) return true;
  return (ACCEPTED_MIME_TYPES as readonly string[]).includes(file.type);
}

function isTerminal(status: RagDocStatus): boolean {
  return status === "ready" || status === "failed";
}

function listIsSettled(docs: RagDocument[]): boolean {
  return docs.every((d) => isTerminal(d.status));
}

function ageLabel(iso: string, t: (k: string, v?: Record<string, string>) => string): string {
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return "";
  const days = Math.floor(
    (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days <= 0) return t("knowledge.card.age.today");
  return t("knowledge.card.age.daysAgo", { n: String(days) });
}

function docIcon(doc: RagDocument): string {
  if (doc.status === "failed") return "error";
  const name = doc.filename.toLowerCase();
  if (name.endsWith(".pdf")) return "picture_as_pdf";
  if (name.endsWith(".docx")) return "article";
  if (name.endsWith(".md")) return "code";
  return "description";
}

// ---- notices ---------------------------------------------------------------

function Notice({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-6">
      <div className="flex gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-container-highest text-on-surface-variant">
          <Icon name={icon} />
        </span>
        <div>
          <h2 className="text-section-head text-on-surface">{title}</h2>
          <div className="mt-2 text-body-sm text-on-surface-variant">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ForbiddenScreen() {
  const { t } = useI18n();
  return (
    <Notice icon="lock" title={t("knowledge.forbidden.title")}>
      {t("knowledge.forbidden.body")}
    </Notice>
  );
}

function NotOnboardedNotice() {
  const { t } = useI18n();
  return (
    <Notice icon="apartment" title={t("notOnboarded.title")}>
      {t("notOnboarded.body")}
    </Notice>
  );
}

function ErrorNotice({ message }: { message: string }) {
  const { t } = useI18n();
  return (
    <Notice icon="error" title={t("error.title")}>
      {message}
    </Notice>
  );
}

// ---- pieces ----------------------------------------------------------------

function QuotaBar({ used, max }: { used: number; max: number }) {
  const { t } = useI18n();
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-label-muted text-on-surface">
          {t("knowledge.storageLabel")}
        </span>
        <span className="text-label-muted text-on-surface-variant">
          {t("knowledge.quota", { used: String(used), max: String(max) })}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container-highest">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: RagDocStatus }) {
  const { t } = useI18n();
  const cls: Record<RagDocStatus, string> = {
    pending: "bg-surface-container-highest text-on-surface-variant",
    embedding: "bg-primary-fixed text-primary",
    ready: "bg-emerald-100 text-emerald-800",
    failed: "bg-error-container text-on-error-container",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${cls[status]}`}
    >
      {t(`knowledge.status.${status}`)}
    </span>
  );
}

function UploadDropZone({ onOpen }: { onOpen: () => void }) {
  const { t } = useI18n();
  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onOpen();
  };
  return (
    <button
      type="button"
      onClick={onOpen}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className="group flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-outline-variant p-8 text-center transition-colors hover:border-primary hover:bg-surface-container-low"
    >
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-highest transition-colors group-hover:bg-primary-fixed">
        <Icon
          name="upload_file"
          className="text-on-surface-variant group-hover:text-primary"
        />
      </span>
      <span className="text-section-head text-on-surface">
        {t("knowledge.upload.zonePrompt")}
      </span>
      <span className="mt-1 text-label-muted text-on-surface-variant">
        {t("knowledge.upload.zoneHint")}
      </span>
    </button>
  );
}

function EmptyState({ onUploadClick }: { onUploadClick: () => void }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center rounded-xl border border-outline-variant bg-surface-container-lowest px-6 py-12 text-center">
      <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-surface-container-highest">
        <Icon
          name="folder_off"
          className="text-[32px] text-on-surface-variant opacity-50"
        />
      </span>
      <h2 className="text-section-head text-on-surface">
        {t("knowledge.empty.title")}
      </h2>
      <p className="mt-2 max-w-md text-body-sm text-on-surface-variant">
        {t("knowledge.empty.body")}
      </p>
      <p className="mt-5 text-body-sm font-medium text-on-surface">
        {t("knowledge.empty.hintsTitle")}
      </p>
      <ul className="mt-2 space-y-1 text-body-sm text-on-surface-variant">
        <li>{t("knowledge.empty.hint1")}</li>
        <li>{t("knowledge.empty.hint2")}</li>
        <li>{t("knowledge.empty.hint3")}</li>
        <li>{t("knowledge.empty.hint4")}</li>
      </ul>
      <div className="mt-6">
        <Button onClick={onUploadClick}>{t("knowledge.empty.cta")}</Button>
      </div>
    </div>
  );
}

function DocRow({
  doc,
  onDelete,
}: {
  doc: RagDocument;
  onDelete: (doc: RagDocument) => void;
}) {
  const { t } = useI18n();
  const facts: string[] = [];
  if (doc.status === "ready") {
    if (doc.page_count != null) {
      facts.push(t("knowledge.card.pages", { count: String(doc.page_count) }));
    }
    if (doc.chunk_count != null) {
      facts.push(
        t("knowledge.card.chunks", { count: String(doc.chunk_count) }),
      );
    }
    facts.push(ageLabel(doc.created_at, t));
  }
  const factLine = facts.filter(Boolean).join(" · ");
  return (
    <li className="group -mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-4 transition-colors hover:bg-surface-container-low">
      <div className="flex min-w-0 items-center gap-4">
        <Icon
          name={docIcon(doc)}
          className={
            doc.status === "failed" ? "text-error" : "text-on-surface-variant"
          }
        />
        <div className="min-w-0">
          <p
            className="truncate text-body-main text-on-surface"
            title={doc.filename}
          >
            {doc.title || doc.filename}
          </p>
          <p className="truncate text-label-muted text-on-surface-variant">
            {doc.filename}
            {factLine && ` · ${factLine}`}
          </p>
          {doc.status === "failed" && doc.error_text && (
            <p className="mt-1 text-label-muted text-error">{doc.error_text}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={doc.status} />
        <button
          type="button"
          onClick={() => onDelete(doc)}
          aria-label={t("knowledge.card.delete")}
          className="rounded-lg p-2 text-on-surface-variant opacity-0 transition-all hover:bg-error-container hover:text-error focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Icon name="delete" className="text-[20px]" />
        </button>
      </div>
    </li>
  );
}

function DocList({
  docs,
  onDelete,
}: {
  docs: RagDocument[];
  onDelete: (doc: RagDocument) => void;
}) {
  const { t } = useI18n();
  return (
    <section>
      <h3 className="border-b border-outline-variant py-2 text-label-muted uppercase tracking-wider text-on-surface-variant">
        {t("knowledge.recentDocs")}
      </h3>
      <ul className="divide-y divide-outline-variant">
        {docs.map((d) => (
          <DocRow key={d.doc_id} doc={d} onDelete={onDelete} />
        ))}
      </ul>
    </section>
  );
}

function ModalShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(29,28,22,0.4)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-outline-variant bg-surface p-6 shadow-soft">
        {children}
      </div>
    </div>
  );
}

// ---- upload modal ----------------------------------------------------------

type QueuedFile = {
  file: File;
  // Set when client-side validation rejects the file before upload.
  clientError: string | null;
  // Filename collision warning (advisory only — server creates a new doc).
  collisionWarning: boolean;
  // Per-file state once upload starts.
  state: "queued" | "uploading" | "sent" | "failed";
  serverError: string | null;
};

function validate(file: File, t: (k: string) => string): string | null {
  if (!isAcceptedFile(file)) return t("knowledge.upload.invalidType");
  if (file.size > MAX_DOC_BYTES) return t("knowledge.upload.tooBig");
  return null;
}

function UploadModal({
  existingFilenames,
  onClose,
  onUploadAll,
}: {
  existingFilenames: Set<string>;
  onClose: () => void;
  onUploadAll: (
    queue: QueuedFile[],
    mutate: (next: QueuedFile[]) => void,
  ) => Promise<void>;
}) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneSummary, setDoneSummary] = useState<
    "" | "all_ok" | "some_failed"
  >("");

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      const next: QueuedFile[] = list.map((file) => {
        const err = validate(file, t);
        return {
          file,
          clientError: err,
          collisionWarning: err == null && existingFilenames.has(file.name),
          state: "queued",
          serverError: null,
        };
      });
      setQueue((prev) => [...prev, ...next]);
    },
    [existingFilenames, t],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) addFiles(e.target.files);
      // Reset input so selecting the same file again re-triggers change.
      e.target.value = "";
    },
    [addFiles],
  );

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  }, []);
  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  }, []);
  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const removeAt = useCallback((idx: number) => {
    setQueue((q) => q.filter((_, i) => i !== idx));
  }, []);

  const canStart = queue.some((q) => q.clientError == null && q.state === "queued");

  const onStart = useCallback(async () => {
    setBusy(true);
    setDoneSummary("");
    // onUploadAll mutates the queue in place via the setQueue setter we
    // hand it; the final state is whatever it last passed back.
    let finalQueue: QueuedFile[] = queue;
    await onUploadAll(queue, (next) => {
      finalQueue = next;
      setQueue(next);
    });
    const failed = finalQueue.some((q) => q.state === "failed");
    setDoneSummary(failed ? "some_failed" : "all_ok");
    setBusy(false);
  }, [onUploadAll, queue]);

  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("knowledge.upload.title")}
      </h2>

      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          "mt-4 rounded-lg border-2 border-dashed p-6 text-center text-body-sm",
          dragging
            ? "border-primary bg-surface-container-low"
            : "border-outline-variant text-on-surface-variant",
        ].join(" ")}
      >
        <p>
          {t("knowledge.upload.dropPrompt")}{" "}
          <button
            type="button"
            className="font-medium text-primary underline"
            onClick={() => inputRef.current?.click()}
          >
            {t("knowledge.upload.browse")}
          </button>
        </p>
        <p className="mt-1 text-label-muted text-on-surface-variant">
          {t("knowledge.upload.accepts")}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.md,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,text/plain"
          onChange={onInputChange}
          className="hidden"
        />
      </div>

      {queue.length > 0 && (
        <div className="mt-4">
          <p className="text-label-muted uppercase tracking-wide text-on-surface-variant">
            {t("knowledge.upload.queued")}
          </p>
          <ul className="mt-2 space-y-2">
            {queue.map((q, idx) => (
              <li
                key={`${q.file.name}-${idx}`}
                className="rounded-lg border border-outline-variant p-3 text-body-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 truncate" title={q.file.name}>
                    {q.file.name}
                  </span>
                  <span className="text-label-muted text-on-surface-variant">
                    {q.state === "uploading" && t("knowledge.upload.sending")}
                    {q.state === "sent" && t("knowledge.upload.sent")}
                    {q.state === "failed" && t("knowledge.upload.failed")}
                  </span>
                  {q.state === "queued" && !busy && (
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      className="text-label-muted text-on-surface-variant hover:text-on-surface"
                    >
                      {t("knowledge.upload.removeFile")}
                    </button>
                  )}
                </div>
                {q.clientError && (
                  <p className="mt-1 text-label-muted text-tertiary">
                    {q.clientError}
                  </p>
                )}
                {!q.clientError && q.collisionWarning && q.state === "queued" && (
                  <p className="mt-1 text-label-muted text-on-surface-variant">
                    {t("knowledge.upload.collision").replace(
                      "{filename}",
                      q.file.name,
                    )}
                  </p>
                )}
                {q.serverError && (
                  <p className="mt-1 text-label-muted text-tertiary">
                    {q.serverError}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {doneSummary === "all_ok" && (
        <p className="mt-4 text-body-sm text-emerald-700">
          {t("knowledge.upload.allDone")}
        </p>
      )}
      {doneSummary === "some_failed" && (
        <p className="mt-4 text-body-sm text-tertiary">
          {t("knowledge.upload.someFailed")}
        </p>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          {doneSummary ? t("knowledge.upload.close") : t("knowledge.upload.cancel")}
        </Button>
        <Button onClick={onStart} disabled={!canStart || busy}>
          {t("knowledge.upload.start")}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- consent + delete modals ----------------------------------------------

function ConsentModal({
  onAccept,
  onCancel,
  busy,
}: {
  onAccept: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  return (
    <ModalShell>
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-fixed text-primary">
        <Icon name="verified_user" />
      </span>
      <h2 className="text-section-head text-on-surface">
        {t("knowledge.consent.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("knowledge.consent.body")}
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("knowledge.consent.cancel")}
        </Button>
        <Button onClick={onAccept} disabled={busy}>
          {t("knowledge.consent.accept")}
        </Button>
      </div>
    </ModalShell>
  );
}

function DeleteModal({
  doc,
  onConfirm,
  onCancel,
  busy,
}: {
  doc: RagDocument;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("knowledge.delete.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("knowledge.delete.body").replace("{filename}", doc.filename)}
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("knowledge.delete.cancel")}
        </Button>
        <Button onClick={onConfirm} disabled={busy}>
          {t("knowledge.delete.confirm")}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- main frame -----------------------------------------------------------

function KnowledgeFrame({
  fetchIdToken,
}: {
  fetchIdToken: IdTokenFetcher;
}) {
  const { t } = useI18n();
  const [me, setMe] = useState<MeResult | null>(null);
  const [list, setList] = useState<RagDocumentList | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RagDocument | null>(null);
  const [busy, setBusy] = useState(false);

  // ----- /me -----
  const reloadMe = useCallback(async () => {
    try {
      const idToken = await fetchIdToken();
      const result = await getMe(idToken);
      setMe(result);
    } catch {
      setLoadError(true);
    }
  }, [fetchIdToken]);

  useEffect(() => {
    void reloadMe();
  }, [reloadMe]);

  // ----- documents list -----
  const reloadList = useCallback(async () => {
    if (!me || me.result !== "ok" || !me.is_onboarding_admin) return;
    try {
      const idToken = await fetchIdToken();
      const result = await listRagDocuments(idToken);
      setList(result);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [fetchIdToken, me]);

  useEffect(() => {
    void reloadList();
  }, [reloadList]);

  // ----- polling -----
  useEffect(() => {
    if (!list || listIsSettled(list.documents)) return;
    const handle = window.setInterval(() => {
      void reloadList();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [list, reloadList]);

  // ----- handlers -----
  const onOpenUpload = useCallback(() => {
    if (!me) return;
    if (me.result === "ok" && !me.consent_acknowledged_at) {
      setConsentOpen(true);
      return;
    }
    setUploadOpen(true);
  }, [me]);

  const onConsentAccept = useCallback(async () => {
    setBusy(true);
    try {
      const idToken = await fetchIdToken();
      await postRagConsent(idToken);
      await reloadMe();
      setConsentOpen(false);
      setUploadOpen(true);
    } catch {
      setLoadError(true);
      setConsentOpen(false);
    } finally {
      setBusy(false);
    }
  }, [fetchIdToken, reloadMe]);

  const onUploadAll = useCallback(
    async (
      queue: QueuedFile[],
      mutate: (next: QueuedFile[]) => void,
    ): Promise<void> => {
      // Upload sequentially so the server-side quota counter and the
      // single shared Postgres pool don't get hammered. v1 typical
      // upload count is small (admin sends a handful of policies).
      const next = [...queue];
      for (let i = 0; i < next.length; i += 1) {
        if (next[i].clientError || next[i].state !== "queued") continue;
        next[i] = { ...next[i], state: "uploading", serverError: null };
        mutate([...next]);
        try {
          const idToken = await fetchIdToken();
          await uploadRagDocument(idToken, next[i].file);
          next[i] = { ...next[i], state: "sent" };
        } catch (err) {
          const e = err as RagUploadError | Error;
          const detail =
            (e as RagUploadError).detail ??
            (e as Error).message ??
            t("error.body");
          next[i] = { ...next[i], state: "failed", serverError: detail };
        }
        mutate([...next]);
      }
      // Trigger a list refresh — pending rows appear so the user sees
      // them transition.
      void reloadList();
    },
    [fetchIdToken, reloadList, t],
  );

  const onDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const idToken = await fetchIdToken();
      await deleteRagDocument(idToken, deleteTarget.doc_id);
      setDeleteTarget(null);
      await reloadList();
    } catch {
      setLoadError(true);
    } finally {
      setBusy(false);
    }
  }, [deleteTarget, fetchIdToken, reloadList]);

  // ----- rendering branches -----
  if (loadError) return <ErrorNotice message={t("knowledge.loadError")} />;
  if (!me) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }
  if (me.result === "not_onboarded") return <NotOnboardedNotice />;
  if (!me.is_onboarding_admin) return <ForbiddenScreen />;
  if (!list) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  const existingFilenames = new Set(
    list.documents.filter((d) => d.status !== "failed").map((d) => d.filename),
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-on-surface">
          {t("knowledge.title")}
        </h1>
        <p className="mt-1 text-body-sm text-on-surface-variant">
          {t("knowledge.subtitle")}
        </p>
      </header>

      <QuotaBar used={list.quota_used} max={list.quota_max} />

      <UploadDropZone onOpen={onOpenUpload} />

      {list.documents.length === 0 ? (
        <EmptyState onUploadClick={onOpenUpload} />
      ) : (
        <DocList docs={list.documents} onDelete={setDeleteTarget} />
      )}

      {consentOpen && (
        <ConsentModal
          onAccept={onConsentAccept}
          onCancel={() => setConsentOpen(false)}
          busy={busy}
        />
      )}
      {uploadOpen && (
        <UploadModal
          existingFilenames={existingFilenames}
          onClose={() => setUploadOpen(false)}
          onUploadAll={onUploadAll}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          doc={deleteTarget}
          onConfirm={onDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

// Two outer variants so `useMsal` is only called when MsalProvider is
// mounted — same pattern as AccountPage.
function KnowledgePageAuthed() {
  const { instance, accounts } = useMsal();
  const fetchIdToken = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) throw new Error("not signed in");
    return acquireIdToken(instance, account);
  }, [instance, accounts]);
  return <KnowledgeFrame fetchIdToken={fetchIdToken} />;
}

function KnowledgePageNoAuth() {
  const fetchIdToken = useMemo(() => async () => null, []);
  return <KnowledgeFrame fetchIdToken={fetchIdToken} />;
}

export function KnowledgePage({
  authConfigured,
}: {
  authConfigured: boolean;
}) {
  return authConfigured ? <KnowledgePageAuthed /> : <KnowledgePageNoAuth />;
}
